import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Comprehensive SEO Component for Mothering Store
 * 
 * Usage:
 * <SEO 
 *   title="Product Name" 
 *   description="Product description"
 *   type="product"
 *   image="https://..."
 * />
 */

export function SEO({
  title = 'Mothering - Quality Products for Children',
  description = 'Shop quality products for children including tiffin boxes, school bags, bottles, toys, educational items and more. Safe, durable, and loved by kids. Free delivery across India.',
  keywords = 'kids products, children items, school bags, tiffin boxes, lunch boxes, water bottles, toys, educational items, baby products, mothering store',
  image = 'https://mothering.in/og-image.jpg',
  url,
  type = 'website', // 'website', 'product', 'article'
  product, // For product pages: { price, currency, availability, condition, brand }
  article, // For blog pages: { publishedTime, modifiedTime, author, tags }
  noindex = false,
  nofollow = false,
  canonical,
}) {
  const siteUrl = 'https://mothering.in';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const canonicalUrl = canonical || fullUrl;

  // Structured data for different page types
  const getStructuredData = () => {
    const baseData = {
      '@context': 'https://schema.org',
    };

    if (type === 'product' && product) {
      return {
        ...baseData,
        '@type': 'Product',
        name: title,
        description: description,
        image: image,
        brand: {
          '@type': 'Brand',
          name: product.brand || 'Mothering',
        },
        offers: {
          '@type': 'Offer',
          price: product.price,
          priceCurrency: product.currency || 'INR',
          availability: product.availability || 'https://schema.org/InStock',
          url: fullUrl,
          priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        },
        aggregateRating: product.rating ? {
          '@type': 'AggregateRating',
          ratingValue: product.rating.value,
          reviewCount: product.rating.count,
        } : undefined,
      };
    }

    if (type === 'article' && article) {
      return {
        ...baseData,
        '@type': 'Article',
        headline: title,
        description: description,
        image: image,
        datePublished: article.publishedTime,
        dateModified: article.modifiedTime || article.publishedTime,
        author: {
          '@type': 'Person',
          name: article.author || 'Mothering Team',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Mothering',
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/logo.png`,
          },
        },
        keywords: article.tags?.join(', '),
      };
    }

    return {
      ...baseData,
      '@type': 'WebPage',
      name: title,
      description: description,
      url: fullUrl,
    };
  };

  // Breadcrumbs structured data
  const getBreadcrumbsData = () => {
    if (!url || url === '/') return null;

    const pathParts = url.split('/').filter(Boolean);
    const breadcrumbs = [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    }];

    let currentPath = '';
    pathParts.forEach((part, index) => {
      currentPath += `/${part}`;
      breadcrumbs.push({
        '@type': 'ListItem',
        position: index + 2,
        name: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
        item: `${siteUrl}${currentPath}`,
      });
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs,
    };
  };

  const robotsContent = `${noindex ? 'noindex' : 'index'},${nofollow ? 'nofollow' : 'follow'}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={robotsContent} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Mothering" />
      <meta property="og:locale" content="en_IN" />

      {/* Product specific OG tags */}
      {type === 'product' && product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content={product.currency || 'INR'} />
          <meta property="product:availability" content={product.availability || 'instock'} />
          <meta property="product:condition" content={product.condition || 'new'} />
          <meta property="product:brand" content={product.brand || 'Mothering'} />
        </>
      )}

      {/* Article specific OG tags */}
      {type === 'article' && article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime || article.publishedTime} />
          <meta property="article:author" content={article.author || 'Mothering Team'} />
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@motheringstore" />

      {/* Additional SEO Tags */}
      <meta name="author" content="Mothering" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      {/* Mobile Optimization */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Mothering" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(getStructuredData())}
      </script>

      {/* Breadcrumbs Structured Data */}
      {getBreadcrumbsData() && (
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbsData())}
        </script>
      )}
    </Helmet>
  );
}

// Presets for common pages
export const SEOPresets = {
  home: {
    title: 'Mothering - Quality Products for Kids | Tiffin, Bags, Bottles & More',
    description: 'Shop safe, durable products for children. Explore tiffin boxes, school bags, water bottles, toys, educational items and more. BPA-free, eco-friendly options. Free delivery across India.',
    keywords: 'kids products india, children items online, school bags for kids, tiffin boxes, lunch boxes india, water bottles kids, toys india, educational toys, baby products, mothering store',
    url: '/',
  },
  
  products: {
    title: 'All Products - Kids Tiffin, Bags, Bottles & Toys | Mothering',
    description: 'Browse our complete collection of quality products for children. Safe tiffin boxes, durable school bags, BPA-free bottles, educational toys and more. Shop now with free delivery.',
    keywords: 'buy kids products, children items online india, school supplies, lunch boxes online, bags for kids, toys online india',
    url: '/products',
  },
  
  categories: (categoryName) => ({
    title: `${categoryName} for Kids | Mothering`,
    description: `Shop ${categoryName.toLowerCase()} for children. Quality, safe, and durable products loved by kids and trusted by parents. Free delivery across India.`,
    keywords: `${categoryName.toLowerCase()} for kids, children ${categoryName.toLowerCase()}, buy ${categoryName.toLowerCase()} online india`,
    url: `/products?category=${categoryName.toLowerCase()}`,
  }),
};
